import React from "react";
import { connect } from "react-redux";
import { Dropdown, Grid, Loader, Dimmer, Button } from "semantic-ui-react";
import DownloadButton from "@buttons/DownloadButton";
import {
  getCategoriesRaw,
  updateCategory,
  loadCategory,
  getAssetTable,
  getAssetsDetailsDownload,
  categoryDownload,
} from "@api/egm";
import { setCreateAssetModal, showSuccessAlert } from "@actions/modals";
import AssetMainTable from "./AssetMainTable";
import BaseSettings from "../systemAdmin/BaseSettings";
import OptionsList from "../systemAdmin/OptionsList";
import AssetDetailView from "./AssetDetailView";
import { ASSET_STATUS_FILTER_DATA } from "@helpers/constant";
import { handleDownload } from "@helpers/downloads";

const CATEGORY_TABLE_COLUMNS = [
  { key: "code", label: "Code", accessor: "categoryCode" },
  { key: "name", label: "Name", accessor: "categoryName" },
  {
    key: "taxRate",
    label: "Tax Rate",
    accessor: "taxRate",
    format: (v) =>
      v != null && v !== "" ? `${(parseFloat(v) * 100).toFixed(2)}%` : "",
  },
  {
    key: "installed",
    label: "Installed",
    accessor: (row) => row.installedCount ?? "-",
  },
  {
    key: "notInstalled",
    label: "Not Installed",
    accessor: (row) => row.notInstalledCount ?? "-",
  },
  { key: "pendingScrapped", label: "Pending Scrapped", accessor: "pendingScrappedCount" },
  { key: "sold", label: "Sold", accessor: "soldCount" },
];

class CategoryView extends BaseSettings {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      operatorId: null,
      categories: [],
      loading: false,
      saving: false,
      selectedCategory: null,
      selectedOptions: {
        main: [],
        ownership: [],
        operational: [],
      },
      selectedNode: null,
      grid: null,
      doQuestion: false,
      doDelete: false,
      questionText: "",
      changed: [],
      showDetail: false,
      assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
      assetDetailAssets: [],
    };
  }

  componentDidUpdate(prevProps) {
    const { createAssetModal } = this.props;
    const prev = prevProps.createAssetModal ?? {};
    if (
      prev.open &&
      !createAssetModal?.open &&
      createAssetModal?.lastCreatedType === "MT"
    ) {
      this.getCategories();
      this.props.setCreateAssetModal({ lastCreatedType: null });
    }
  }

  onOperatorChange = (e, { value }) => {
    this.setState(
      {
        operatorId: value,
        selectedCategory: null,
        assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
        assetDetailAssets: [],
      },
      () => {
        if (value) {
          this.getCategories();
        }
      },
    );
  };

  getAssetTableData = (categoryId, statusOption) => {
    const { operatorId } = this.state;

    return getAssetTable(
      operatorId,
      categoryId,
      -1,
      -1,
      -1,
      -1,
      statusOption,
      -1,
      0,
      "#",
      -1,
      -1,
      -1,
      true,
    );
  };

  handleAssetStatusOptionChange = (option) => {
    const { selectedCategory } = this.state;
    if (!selectedCategory) return;

    this.setState({ assetDetailStatusOption: option, loading: true }, () => {
      this.getAssetTableData(selectedCategory.categoryId, option)
        .then((tableData) => {
          this.setState({
            assetDetailAssets: tableData.assets || [],
            loading: false,
          });
        })
        .catch(() => this.setState({ loading: false }));
    });
  };

  getCategories = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.setState({ loading: true }, () => {
      getCategoriesRaw(operatorId)
        .then((data) => {
          this.setState({
            categories: data,
            loading: false,
          });
        })
        .catch(() => this.setState({ loading: false }));
    });
  };

  componentDidMount() {
    const { operators } = this.props;
    if (operators.length === 1 && operators[0].value !== -1) {
      this.setState({ operatorId: operators[0].value }, this.getCategories);
    }
  }

  handleNew = (selectedOptions, selectedNode, grid = null) => {
    this.setState({
      selectedOptions: selectedOptions.main
        ? selectedOptions
        : { main: selectedOptions, ownership: [], operational: [] },
      changed: [],
      selectedNode,
      grid,
    });
  };

  onSelectRow = (item) => {
    const defaultStatus = ASSET_STATUS_FILTER_DATA[0].value;
    this.setState({
      loading: true,
      showDetail: true,
      assetDetailStatusOption: defaultStatus,
    });
    Promise.all([
      loadCategory(item.categoryId),
      this.getAssetTableData(item.categoryId, defaultStatus),
    ])
      .then(([response, tableData]) => {
        const { doQuestion, doDelete, questionText } = response;
        this.setState(
          {
            doQuestion,
            doDelete,
            questionText,
            selectedCategory: item,
            assetDetailAssets: tableData.assets || [],
          },
          () => {
            this.handleNew(
              response.assetUI
                ? response.assetUI
                : {
                    main: response.options || response.main || [],
                    operational: [],
                    ownership: [],
                  },
              item,
              response.grid,
            );
            this.setState({ loading: false });
          },
        );
      })
      .catch(() => this.setState({ loading: false }));
  };

  onNew = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.props.setCreateAssetModal({
      open: true,
      type: "MT",
      operatorId,
      name: "New Category",
    });
  };

  onSave = () => {
    const { operatorId, selectedCategory, selectedOptions } = this.state;
    const { showSuccessAlert } = this.props;
    if (!operatorId || !selectedCategory) return;
    const categoryId = selectedCategory ? selectedCategory.categoryId : 0;
    const changed = this.getAll(selectedOptions);

    this.setState({ saving: true }, () => {
      updateCategory(categoryId, operatorId, changed, () =>
        this.getCategories(),
      )
        .then(() => {
          this.setState({ saving: false }, () => {
            showSuccessAlert(
              categoryId
                ? "Category updated successfully."
                : "Category created successfully.",
            );
          });
        })
        .catch(() => this.setState({ saving: false }));
    });
  };

  handleDownload = () => {
    const { operatorId } = this.state;
    this.setState({ loading: true }, () => {
      categoryDownload(operatorId)
      .then(
        (response) => {
          this.setState({ loading: false }, () => handleDownload(response));
        },
        () => this.setState({ loading: false }),
      );
    });
  };

  handleAssetDetailDownload = () => {
    const { operatorId, selectedCategory, assetDetailStatusOption } =
      this.state;
    if (!operatorId || !selectedCategory) return;

    this.setState({ loading: true }, () => {
      getAssetsDetailsDownload(
        operatorId,
        selectedCategory.categoryId,
        -1,
        -1,
        -1,
        -1,
        assetDetailStatusOption,
        -1,
        0,
        "#",
        -1,
        -1,
        -1,
        true,
      ).then(
        (response) => {
          this.setState({ loading: false }, () => handleDownload(response));
        },
        () => this.setState({ loading: false }),
      );
    });
  };

  render() {
    const {
      categories,
      loading,
      saving,
      selectedCategory,
      selectedOptions,
      changed,
    } = this.state;

    const canSave =
      !!selectedCategory &&
      this.isAllValid(
        selectedOptions || { main: [], ownership: [], operational: [] },
      ) && changed.length > 0

    return (
      <div className="flex-stretch gs-flex-container-vertical height-100">
        <Grid className="height-100 flex-stretch">
          <Grid.Row className={`flex-stretch ${this.state.showDetail ? "h-50" : "h-100"}`}>
            <Grid.Column width={12} className="height-100 flex">
              {loading && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}
              <div className="mb-0_5">
                <Dropdown
                  placeholder="Select operator"
                  selection
                  options={this.props.operators}
                  value={this.state.operatorId || ""}
                  onChange={this.onOperatorChange}
                  disabled={!this.props.operators.length}
                />
                <Button
                  className="ml-0_5"
                  icon="expand"
                  basic={!this.state.showDetail}
                  color="grey"
                  secondary
                  onClick={() => this.setState({ showDetail: !this.state.showDetail })}
                  size="mini"
                  disabled={!selectedCategory}
                />
              </div>
              <div className="mb-0_5 flex-stretch">
                <AssetMainTable
                  columns={CATEGORY_TABLE_COLUMNS}
                  data={categories}
                  rowKey="categoryId"
                  selectedRowId={selectedCategory?.categoryId}
                  onSelectRow={this.onSelectRow}
              />
              </div>              
            </Grid.Column>
            <Grid.Column width={4} className="height-100 flex">
              <div className="flex-start">
                <Button
                  color="green"
                  content="New"
                  onClick={this.onNew}
                  disabled={!this.state.operatorId}
                  loading={loading}
                />
                <Button
                  primary
                  content="Save"
                  onClick={this.onSave}
                  disabled={!canSave}
                  loading={saving}
                />
                <DownloadButton
                  disabled={!this.state.operatorId}
                  caption="Export"
                  onClick={this.handleDownload}
                  loading={loading}
                />
              </div>
              <div className="flex-stretch height-100 p-1 border-grey-1 flex">
                <OptionsList
                  options={this.mapChanged(
                    (selectedOptions && selectedOptions.main) || [],
                  )}
                  onChange={this.handleChangeValue}
                />
              </div>
              {loading && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}                
            </Grid.Column>
          </Grid.Row>
          {this.state.showDetail && (
            <Grid.Row className="flex-stretch h-50">
              <Grid.Column width={16}>
                <AssetDetailView
                  statusOption={this.state.assetDetailStatusOption}
                  assets={this.state.assetDetailAssets}
                  loading={this.state.loading}
                  operatorId={this.state.operatorId}
                  detailExportDisabled={!this.state.selectedCategory}
                  onStatusOptionChange={this.handleAssetStatusOptionChange}
                  onDownloadClick={this.handleAssetDetailDownload}
                />
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  operators: state.operators?.filter((x) => x.value !== -1) ?? [],
  createAssetModal: state.createAssetModal,
});

export default connect(mapStateToProps, {
  setCreateAssetModal,
  showSuccessAlert,
})(CategoryView);
